export type KunosLiteVehicle = {
  geometry: { lengthM: number, widthM: number, heightM: number, wheelbaseM: number, wheelRadiusM: number, groundClearanceM: number, frontTrackM?: number, rearTrackM?: number }
  suspension: { front: { travelM: number, naturalFrequencyHz: number, dampingRatio: number }, rear: { travelM: number, naturalFrequencyHz: number, dampingRatio: number }, antiRollFront: number, antiRollRear: number }
  body: { massKg: number, frontWeightFraction: number, centreOfMassHeightM: number, yawInertiaScale: number }
  engine: { idleRpm: number, limiterRpm: number, engineBraking: number, inertiaKgM2?: number, torqueCurve: [number, number][] }
  drivetrain: { type: 'RWD' | 'FWD' | 'AWD', efficiency: number, finalDrive: number, gears: number[], shiftTimeSeconds: number, differentialPower?: number, differentialCoast?: number, differentialPreload?: number }
  tyres: { grip: number, frontGripMultiplier?: number, rearGripMultiplier?: number, loadSensitivity: number, peakSlipAngleDegrees: number, peakSlipRatio: number, slidingGripFraction: number, longitudinalSlidingGripFraction?: number, postPeakFalloff?: number, peakPlateauNormalized?: number, combinedSlipExponent?: number, rollingResistance: number, wheelInertiaKgM2?: number, longitudinalRelaxationLengthM?: number, lateralRelaxationLengthM?: number, minimumRelaxationTimeSeconds?: number, maximumRelaxationTimeSeconds?: number }
  steering: { maximumAngleDegrees: number, response: number, slipAngleLimitMultiplier: number, slipAngleLimitStartSpeedMps: number, slipAngleLimitFullEffectSpeedMps: number, ackermannPercent?: number }
  brakes: { maximumTorqueNm: number, frontBias: number, handbrakeTorqueNm: number }
  aero: { dragArea: number, downforceCoefficient: number, frontBalance: number }
  assists: { absAvailable: boolean, tractionControlAvailable: boolean }
}

export type KunosLiteWheelInput = { contact: boolean, loadN: number, surfaceGrip?: number }
export type KunosLiteInput = { drive: number, steer: number, handbrake: boolean, wheels?: readonly KunosLiteWheelInput[], frontContact?: number, rearContact?: number }
export type KunosLiteWheelState = {
  index: number, axle: 'front' | 'rear', side: 'left' | 'right', longitudinalPositionM: number, lateralPositionM: number
  contact: boolean, normalLoadN: number, surfaceGrip: number, steeringAngleRadians: number
  angularVelocityRadPerSecond: number, rotationRadians: number, longitudinalPatchSpeedMps: number, lateralPatchSpeedMps: number
  slipRatio: number, slipAngleDegrees: number, frictionCoefficient: number, longitudinalForceN: number, lateralForceN: number
  gripUsage: number, driveTorqueNm: number, brakeTorqueNm: number
}
export type KunosLiteState = {
  velocityX: number, velocityZ: number, yawRate: number, steeringAngle: number, engineRpm: number, gear: number
  shiftRemainingSeconds: number, shiftCooldownSeconds: number, longitudinalSpeed: number, lateralSpeed: number
  longitudinalAcceleration: number, lateralAcceleration: number, steeringSlipLimitDegrees: number
  longitudinalForceN: number, lateralForceN: number, wheels: KunosLiteWheelState[]
  frontSlipAngleDegrees: number, rearSlipAngleDegrees: number, frontLateralForceN: number, rearLateralForceN: number
  frontGripUsage: number, rearGripUsage: number
}
export type KunosLiteStep = { deltaX: number, deltaZ: number, headingDelta: number }
export type KunosLiteTyreSample = {
  longitudinalForceFactor: number, lateralForceFactor: number, pureLongitudinalFactor: number, pureLateralFactor: number
  combinedSlipNormalized: number, forceUtilization: number
}

const gravity = 9.81
const airDensity = 1.225
const wheelCount = 4
const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value))
const smoothstep = (minimum: number, maximum: number, value: number) => {
  const factor = clamp((value - minimum) / Math.max(maximum - minimum, 0.001), 0, 1)
  return factor * factor * (3 - 2 * factor)
}
const superellipse = (x: number, y: number, exponent: number) => (
  Math.abs(x) ** exponent + Math.abs(y) ** exponent
) ** (1 / exponent)

function wheelLayout(vehicle: KunosLiteVehicle) {
  const rearToCom = vehicle.geometry.wheelbaseM * vehicle.body.frontWeightFraction
  const frontToCom = vehicle.geometry.wheelbaseM - rearToCom
  const frontHalfTrack = (vehicle.geometry.frontTrackM ?? vehicle.geometry.widthM * 0.86) * 0.5
  const rearHalfTrack = (vehicle.geometry.rearTrackM ?? vehicle.geometry.widthM * 0.87) * 0.5
  return [
    { axle: 'front' as const, side: 'left' as const, longitudinal: frontToCom, lateral: -frontHalfTrack },
    { axle: 'front' as const, side: 'right' as const, longitudinal: frontToCom, lateral: frontHalfTrack },
    { axle: 'rear' as const, side: 'left' as const, longitudinal: -rearToCom, lateral: -rearHalfTrack },
    { axle: 'rear' as const, side: 'right' as const, longitudinal: -rearToCom, lateral: rearHalfTrack },
  ]
}

function createWheelStates(vehicle: KunosLiteVehicle): KunosLiteWheelState[] {
  return wheelLayout(vehicle).map((layout, index) => ({
    index, axle: layout.axle, side: layout.side, longitudinalPositionM: layout.longitudinal, lateralPositionM: layout.lateral,
    contact: true,
    normalLoadN: vehicle.body.massKg * gravity * (layout.axle === 'front' ? vehicle.body.frontWeightFraction : 1 - vehicle.body.frontWeightFraction) * 0.5,
    surfaceGrip: 1, steeringAngleRadians: 0, angularVelocityRadPerSecond: 0, rotationRadians: 0,
    longitudinalPatchSpeedMps: 0, lateralPatchSpeedMps: 0, slipRatio: 0, slipAngleDegrees: 0,
    frictionCoefficient: vehicle.tyres.grip, longitudinalForceN: 0, lateralForceN: 0, gripUsage: 0,
    driveTorqueNm: 0, brakeTorqueNm: 0,
  }))
}

export function createKunosLiteState(vehicle: KunosLiteVehicle): KunosLiteState {
  return {
    velocityX: 0, velocityZ: 0, yawRate: 0, steeringAngle: 0, engineRpm: vehicle.engine.idleRpm, gear: 1,
    shiftRemainingSeconds: 0, shiftCooldownSeconds: 0, longitudinalSpeed: 0, lateralSpeed: 0,
    longitudinalAcceleration: 0, lateralAcceleration: 0, steeringSlipLimitDegrees: vehicle.steering.maximumAngleDegrees,
    longitudinalForceN: 0, lateralForceN: 0, wheels: createWheelStates(vehicle),
    frontSlipAngleDegrees: 0, rearSlipAngleDegrees: 0, frontLateralForceN: 0, rearLateralForceN: 0,
    frontGripUsage: 0, rearGripUsage: 0,
  }
}

export function resetKunosLiteState(state: KunosLiteState, vehicle: KunosLiteVehicle) {
  Object.assign(state, createKunosLiteState(vehicle))
}

function torqueAtRpm(vehicle: KunosLiteVehicle, rpm: number) {
  const curve = vehicle.engine.torqueCurve
  if (rpm <= curve[0][0]) return curve[0][1]
  for (let index = 1; index < curve.length; index += 1) {
    if (rpm > curve[index][0]) continue
    const [lowerRpm, lowerTorque] = curve[index - 1]
    const [upperRpm, upperTorque] = curve[index]
    return lowerTorque + (upperTorque - lowerTorque) * (rpm - lowerRpm) / (upperRpm - lowerRpm)
  }
  return curve[curve.length - 1][1]
}

function pureTyreResponse(normalizedSlip: number, slidingFraction: number, falloff: number, shoulder: number) {
  if (normalizedSlip <= 0) return 0
  if (normalizedSlip <= 1) return Math.sin(normalizedSlip * Math.PI * 0.5)
  const overslip = Math.max(0, normalizedSlip - 1 - Math.max(0, shoulder))
  return slidingFraction + (1 - slidingFraction) * Math.exp(-Math.max(0.01, falloff) * overslip ** 2)
}

/** Pure forces are combined in force space. Raw slip no longer dictates the force direction. */
export function sampleKunosLiteTyre(vehicle: KunosLiteVehicle, slipRatio: number, slipAngleRadians: number): KunosLiteTyreSample {
  const peakRatio = Math.max(vehicle.tyres.peakSlipRatio, 0.001)
  const peakAngle = Math.max(vehicle.tyres.peakSlipAngleDegrees * Math.PI / 180, 0.001)
  const normalizedLongitudinal = Math.abs(slipRatio) / peakRatio
  const normalizedLateral = Math.abs(Math.tan(slipAngleRadians)) / Math.max(Math.tan(peakAngle), 0.001)
  const falloff = vehicle.tyres.postPeakFalloff ?? 0.45
  const shoulder = vehicle.tyres.peakPlateauNormalized ?? 0.35
  const pureLongitudinal = pureTyreResponse(normalizedLongitudinal,
    vehicle.tyres.longitudinalSlidingGripFraction ?? Math.max(vehicle.tyres.slidingGripFraction, 0.82), falloff, shoulder)
  const pureLateral = pureTyreResponse(normalizedLateral, vehicle.tyres.slidingGripFraction, falloff, shoulder)
  const exponent = clamp(vehicle.tyres.combinedSlipExponent ?? 2, 1.5, 2)
  const forceUtilization = superellipse(pureLongitudinal, pureLateral, exponent)
  const envelopeScale = Math.max(1, forceUtilization)
  return {
    longitudinalForceFactor: Math.sign(slipRatio) * pureLongitudinal / envelopeScale,
    lateralForceFactor: -Math.sign(slipAngleRadians) * pureLateral / envelopeScale,
    pureLongitudinalFactor: pureLongitudinal,
    pureLateralFactor: pureLateral,
    combinedSlipNormalized: superellipse(normalizedLongitudinal, normalizedLateral, exponent),
    forceUtilization,
  }
}

function localSpeeds(state: KunosLiteState, heading: number) {
  const sin = Math.sin(heading)
  const cos = Math.cos(heading)
  return { longitudinal: state.velocityX * sin + state.velocityZ * cos, lateral: state.velocityX * cos - state.velocityZ * sin }
}

function drivenWheelIndices(vehicle: KunosLiteVehicle) {
  return vehicle.drivetrain.type === 'FWD' ? [0, 1] : vehicle.drivetrain.type === 'AWD' ? [0, 1, 2, 3] : [2, 3]
}

function frontWheelSteeringAngle(vehicle: KunosLiteVehicle, centreAngle: number, index: number) {
  const magnitude = Math.abs(centreAngle)
  if (magnitude < 0.0001) return 0
  const halfTrack = (vehicle.geometry.frontTrackM ?? vehicle.geometry.widthM * 0.86) * 0.5
  const turnRadius = vehicle.geometry.wheelbaseM / Math.max(Math.tan(magnitude), 0.001)
  const inner = Math.atan(vehicle.geometry.wheelbaseM / Math.max(0.1, turnRadius - halfTrack))
  const outer = Math.atan(vehicle.geometry.wheelbaseM / (turnRadius + halfTrack))
  const leftIsInner = centreAngle > 0
  const ackermannAngle = index === 0 ? (leftIsInner ? inner : outer) : (leftIsInner ? outer : inner)
  const ackermann = clamp(vehicle.steering.ackermannPercent ?? 0.85, 0, 1)
  return Math.sign(centreAngle) * (magnitude + (ackermannAngle - magnitude) * ackermann)
}

function wheelContactInput(vehicle: KunosLiteVehicle, input: KunosLiteInput, index: number) {
  const supplied = input.wheels?.[index]
  if (supplied) return { contact: supplied.contact, loadN: Math.max(0, supplied.loadN), surfaceGrip: Math.max(0, supplied.surfaceGrip ?? 1) }
  const front = index < 2
  const contact = clamp(front ? input.frontContact ?? 1 : input.rearContact ?? 1, 0, 1)
  const staticLoad = vehicle.body.massKg * gravity * (front ? vehicle.body.frontWeightFraction : 1 - vehicle.body.frontWeightFraction) * 0.5
  return { contact: contact > 0, loadN: staticLoad * contact, surfaceGrip: 1 }
}

function requestedSteeringAngle(state: KunosLiteState, vehicle: KunosLiteVehicle, input: number, longitudinal: number, lateral: number) {
  const desired = input * vehicle.steering.maximumAngleDegrees * Math.PI / 180
  if (Math.abs(desired) < 0.0001) return 0
  const travelSign = longitudinal < -0.2 ? -1 : 1
  const frontPosition = wheelLayout(vehicle)[0].longitudinal
  const noSteerSlip = Math.atan2(lateral + frontPosition * state.yawRate, Math.max(Math.abs(longitudinal), 2.5))
  const desiredSlip = noSteerSlip - desired * travelSign
  const targetSlip = vehicle.tyres.peakSlipAngleDegrees * Math.PI / 180 * vehicle.steering.slipAngleLimitMultiplier
  let limited = desired
  if (Math.abs(desiredSlip) > targetSlip && Math.abs(desiredSlip) > Math.abs(noSteerSlip)) {
    const candidate = (noSteerSlip - Math.sign(desiredSlip) * targetSlip) / travelSign
    limited = Math.sign(candidate) === Math.sign(desired) ? Math.sign(desired) * Math.min(Math.abs(desired), Math.abs(candidate)) : 0
  }
  const effect = smoothstep(vehicle.steering.slipAngleLimitStartSpeedMps,
    vehicle.steering.slipAngleLimitFullEffectSpeedMps, Math.abs(longitudinal))
  return desired + (limited - desired) * effect
}

function selectForwardGear(state: KunosLiteState, vehicle: KunosLiteVehicle, roadSpeed: number) {
  if (state.gear < 1) state.gear = 1
  if (state.shiftRemainingSeconds > 0 || state.shiftCooldownSeconds > 0) return
  const maximumGear = vehicle.drivetrain.gears.length - 2
  const ratio = vehicle.drivetrain.gears[state.gear + 1]
  const roadRpm = roadSpeed / vehicle.geometry.wheelRadiusM * ratio * vehicle.drivetrain.finalDrive * 60 / (2 * Math.PI)
  if (state.engineRpm > vehicle.engine.limiterRpm - 350 && roadRpm > vehicle.engine.limiterRpm - 900 && state.gear < maximumGear) {
    state.gear += 1
    state.shiftRemainingSeconds = vehicle.drivetrain.shiftTimeSeconds
    state.shiftCooldownSeconds = Math.max(0.65, vehicle.drivetrain.shiftTimeSeconds * 2)
  } else if (roadRpm < 1_900 && state.gear > 1) {
    const candidate = state.gear - 1
    const candidateRatio = vehicle.drivetrain.gears[candidate + 1]
    const candidateRpm = roadSpeed / vehicle.geometry.wheelRadiusM * candidateRatio * vehicle.drivetrain.finalDrive * 60 / (2 * Math.PI)
    if (candidateRpm < vehicle.engine.limiterRpm - 500) {
      state.gear = candidate
      state.shiftRemainingSeconds = vehicle.drivetrain.shiftTimeSeconds
      state.shiftCooldownSeconds = Math.max(0.65, vehicle.drivetrain.shiftTimeSeconds * 2)
    }
  }
}

function distributeDifferentialTorque(vehicle: KunosLiteVehicle, state: KunosLiteState, torques: number[], driven: number[], powered: boolean) {
  const pairs = driven.length === 4 ? [[0, 1], [2, 3]] : driven.length === 2 ? [[driven[0], driven[1]]] : []
  for (const [left, right] of pairs) {
    const difference = state.wheels[left].angularVelocityRadPerSecond - state.wheels[right].angularVelocityRadPerSecond
    if (Math.abs(difference) < 0.01) continue
    const lock = clamp(powered ? vehicle.drivetrain.differentialPower ?? 0 : vehicle.drivetrain.differentialCoast ?? 0, 0, 1)
    const axleTorque = Math.abs(torques[left]) + Math.abs(torques[right])
    const transfer = Math.min(Math.min(Math.abs(torques[left]), Math.abs(torques[right])) * 0.8,
      (vehicle.drivetrain.differentialPreload ?? 0) * 100 + axleTorque * lock * 0.18)
    torques[left] -= Math.sign(difference) * transfer
    torques[right] += Math.sign(difference) * transfer
  }
}

function simulateSubstep(state: KunosLiteState, vehicle: KunosLiteVehicle, input: KunosLiteInput, heading: number, delta: number) {
  const mass = vehicle.body.massKg
  const yawInertia = mass * (vehicle.geometry.wheelbaseM ** 2 + vehicle.geometry.widthM ** 2) / 12 * vehicle.body.yawInertiaScale
  const sin = Math.sin(heading)
  const cos = Math.cos(heading)
  const local = localSpeeds(state, heading)
  const longitudinal = local.longitudinal
  const lateral = local.lateral

  const steeringTarget = requestedSteeringAngle(state, vehicle, input.steer, longitudinal, lateral)
  state.steeringSlipLimitDegrees = Math.abs(steeringTarget) * 180 / Math.PI
  state.steeringAngle += (steeringTarget - state.steeringAngle) * (1 - Math.exp(-vehicle.steering.response * delta))

  const wantsForward = input.drive > 0.001
  const wantsReverse = input.drive < -0.001
  const braking = (wantsReverse && longitudinal > 0.6) || (wantsForward && longitudinal < -0.6)
  const propulsion = braking ? 0 : Math.abs(input.drive)
  const brakeInput = braking ? Math.abs(input.drive) : 0
  const roadSpeed = Math.hypot(state.velocityX, state.velocityZ)
  if (!braking && wantsForward) selectForwardGear(state, vehicle, roadSpeed)
  if (!braking && wantsReverse && Math.abs(longitudinal) <= 0.75) state.gear = -1
  state.shiftRemainingSeconds = Math.max(0, state.shiftRemainingSeconds - delta)
  state.shiftCooldownSeconds = Math.max(0, state.shiftCooldownSeconds - delta)

  const driveDirection = state.gear < 0 ? -1 : 1
  const ratio = state.gear < 0 ? Math.abs(vehicle.drivetrain.gears[0]) : vehicle.drivetrain.gears[state.gear + 1]
  const driven = drivenWheelIndices(vehicle)
  const drivenAngularSpeed = driven.reduce((sum, index) => sum + Math.abs(state.wheels[index].angularVelocityRadPerSecond), 0) / driven.length
  const coupledRpm = drivenAngularSpeed * ratio * vehicle.drivetrain.finalDrive * 60 / (2 * Math.PI)
  const freeRevRpm = vehicle.engine.idleRpm + propulsion * (vehicle.engine.limiterRpm - vehicle.engine.idleRpm) * 0.18
  state.engineRpm = clamp(Math.max(coupledRpm, Math.abs(longitudinal) < 1.5 ? freeRevRpm : 0), vehicle.engine.idleRpm, vehicle.engine.limiterRpm)

  const driveTorques = new Array<number>(wheelCount).fill(0)
  if (propulsion > 0 && state.shiftRemainingSeconds <= 0) {
    const limiterFactor = clamp((vehicle.engine.limiterRpm - state.engineRpm) / 180, 0, 1)
    const axleTorque = torqueAtRpm(vehicle, state.engineRpm) * ratio * vehicle.drivetrain.finalDrive
      * vehicle.drivetrain.efficiency * propulsion * limiterFactor * driveDirection
    for (const index of driven) driveTorques[index] = axleTorque / driven.length
  } else if (propulsion === 0 && Math.abs(longitudinal) > 0.1) {
    const coastTorque = torqueAtRpm(vehicle, state.engineRpm) * vehicle.engine.engineBraking
      * ratio * vehicle.drivetrain.finalDrive * vehicle.drivetrain.efficiency
    for (const index of driven) {
      driveTorques[index] = -Math.sign(state.wheels[index].angularVelocityRadPerSecond || longitudinal) * coastTorque / driven.length
    }
  }
  distributeDifferentialTorque(vehicle, state, driveTorques, driven, propulsion > 0)

  const frontBrakeTorque = vehicle.brakes.maximumTorqueNm * brakeInput
  const rearBrakeTorque = frontBrakeTorque * (1 - vehicle.brakes.frontBias) / Math.max(vehicle.brakes.frontBias, 0.01)
  const brakeTorques = [frontBrakeTorque, frontBrakeTorque, rearBrakeTorque, rearBrakeTorque]
  if (input.handbrake) { brakeTorques[2] += vehicle.brakes.handbrakeTorqueNm; brakeTorques[3] += vehicle.brakes.handbrakeTorqueNm }

  const speedSquared = state.velocityX ** 2 + state.velocityZ ** 2
  const downforce = 0.5 * airDensity * vehicle.aero.downforceCoefficient * speedSquared
  const referenceFrontLoad = mass * gravity * vehicle.body.frontWeightFraction * 0.5
  const referenceRearLoad = mass * gravity * (1 - vehicle.body.frontWeightFraction) * 0.5
  const minimumRelaxation = vehicle.tyres.minimumRelaxationTimeSeconds ?? 0.04
  const maximumRelaxation = Math.max(minimumRelaxation, vehicle.tyres.maximumRelaxationTimeSeconds ?? 0.055)
  let bodyLongitudinalForce = 0
  let bodyLateralForce = 0
  let yawMoment = 0

  for (let index = 0; index < wheelCount; index += 1) {
    const wheel = state.wheels[index]
    const front = wheel.axle === 'front'
    const contactInput = wheelContactInput(vehicle, input, index)
    const inputLoad = contactInput.contact ? contactInput.loadN
      + downforce * (front ? vehicle.aero.frontBalance : 1 - vehicle.aero.frontBalance) * 0.5 : 0
    const referenceLoad = front ? referenceFrontLoad : referenceRearLoad
    wheel.contact = contactInput.contact && inputLoad >= Math.max(25, referenceLoad * 0.02)
    wheel.normalLoadN = wheel.contact ? inputLoad : 0
    wheel.surfaceGrip = contactInput.surfaceGrip
    wheel.steeringAngleRadians = front ? frontWheelSteeringAngle(vehicle, state.steeringAngle, index) : 0
    wheel.driveTorqueNm = driveTorques[index]

    let brakeTorque = brakeTorques[index]
    const serviceAbs = vehicle.assists.absAvailable && brakeInput > 0 && !(input.handbrake && !front)
    if (serviceAbs && wheel.slipRatio < -vehicle.tyres.peakSlipRatio) {
      const overslip = (-wheel.slipRatio - vehicle.tyres.peakSlipRatio) / Math.max(vehicle.tyres.peakSlipRatio * 1.5, 0.01)
      brakeTorque *= clamp(1 - overslip * 0.82, 0.18, 1)
    }
    wheel.brakeTorqueNm = brakeTorque

    const patchBodyLong = longitudinal - wheel.lateralPositionM * state.yawRate
    const patchBodyLat = lateral + wheel.longitudinalPositionM * state.yawRate
    const steerCos = Math.cos(wheel.steeringAngleRadians)
    const steerSin = Math.sin(wheel.steeringAngleRadians)
    const patchLong = patchBodyLong * steerCos + patchBodyLat * steerSin
    const patchLat = -patchBodyLong * steerSin + patchBodyLat * steerCos
    wheel.longitudinalPatchSpeedMps = patchLong
    wheel.lateralPatchSpeedMps = patchLat
    const slipAngle = Math.atan2(patchLat, Math.max(Math.abs(patchLong), 1))
    wheel.slipAngleDegrees = slipAngle * 180 / Math.PI

    const axleGrip = front ? vehicle.tyres.frontGripMultiplier ?? 1 : vehicle.tyres.rearGripMultiplier ?? 1
    wheel.frictionCoefficient = wheel.contact ? vehicle.tyres.grip * axleGrip * contactInput.surfaceGrip
      * Math.pow(referenceLoad / Math.max(wheel.normalLoadN, 1), vehicle.tyres.loadSensitivity) : 0
    const frictionLimit = wheel.normalLoadN * wheel.frictionCoefficient
    const baseWheelInertia = vehicle.tyres.wheelInertiaKgM2 ?? 1.35
    const reflectedEngineInertia = driven.includes(index) && state.shiftRemainingSeconds <= 0
      ? (vehicle.engine.inertiaKgM2 ?? 0) * (ratio * vehicle.drivetrain.finalDrive) ** 2 / driven.length : 0
    const wheelInertia = baseWheelInertia + reflectedEngineInertia
    const brakeDirection = Math.sign(wheel.angularVelocityRadPerSecond
      || patchLong / vehicle.geometry.wheelRadiusM
      || wheel.driveTorqueNm)
    const externalTorque = wheel.driveTorqueNm - brakeDirection * wheel.brakeTorqueNm
    const oldAngularVelocity = wheel.angularVelocityRadPerSecond
    const freeAngularVelocity = oldAngularVelocity + externalTorque / wheelInertia * delta
    const slipDenominator = Math.max(Math.abs(patchLong), 1.5)
    const relaxationSpeed = Math.max(Math.abs(patchLong), 2)
    const longitudinalTime = Math.min(maximumRelaxation, Math.max(minimumRelaxation * 0.65,
      (vehicle.tyres.longitudinalRelaxationLengthM ?? 0.3) / relaxationSpeed))
    const lateralTime = Math.min(maximumRelaxation, Math.max(minimumRelaxation,
      (vehicle.tyres.lateralRelaxationLengthM ?? 0.4) / relaxationSpeed))
    const longitudinalBlend = wheel.contact ? 1 - Math.exp(-delta / longitudinalTime) : 1
    const lateralBlend = wheel.contact ? 1 - Math.exp(-delta / lateralTime) : 1
    const previousLongitudinalForce = wheel.longitudinalForceN
    const previousLateralForce = wheel.lateralForceN
    const forceAtAngularVelocity = (angularVelocity: number) => {
      const slipRatio = (angularVelocity * vehicle.geometry.wheelRadiusM - patchLong) / slipDenominator
      const sample = sampleKunosLiteTyre(vehicle, slipRatio, slipAngle)
      return {
        slipRatio,
        usage: sample.combinedSlipNormalized,
        longitudinal: previousLongitudinalForce + (sample.longitudinalForceFactor * frictionLimit - previousLongitudinalForce) * longitudinalBlend,
        targetLateral: sample.lateralForceFactor * frictionLimit,
      }
    }

    let solvedAngularVelocity = freeAngularVelocity
    if (wheel.contact && frictionLimit > 0) {
      const maximumTyreDelta = frictionLimit * vehicle.geometry.wheelRadiusM / wheelInertia * delta
      let lower = freeAngularVelocity - maximumTyreDelta * 1.2 - 0.001
      let upper = freeAngularVelocity + maximumTyreDelta * 1.2 + 0.001
      const residual = (angularVelocity: number) => angularVelocity - oldAngularVelocity
        - (externalTorque - forceAtAngularVelocity(angularVelocity).longitudinal * vehicle.geometry.wheelRadiusM) / wheelInertia * delta
      let lowerResidual = residual(lower)
      let upperResidual = residual(upper)
      if (lowerResidual > upperResidual) { [lower, upper] = [upper, lower]; [lowerResidual, upperResidual] = [upperResidual, lowerResidual] }
      if (lowerResidual <= 0 && upperResidual >= 0) {
        for (let iteration = 0; iteration < 14; iteration += 1) {
          const middle = (lower + upper) * 0.5
          if (residual(middle) > 0) upper = middle
          else lower = middle
        }
        solvedAngularVelocity = (lower + upper) * 0.5
      }
    }
    if (wheel.brakeTorqueNm > 0 && brakeDirection !== 0 && solvedAngularVelocity * brakeDirection < 0) {
      solvedAngularVelocity = 0
    }
    wheel.angularVelocityRadPerSecond = solvedAngularVelocity
    const tyreForce = forceAtAngularVelocity(solvedAngularVelocity)
    wheel.slipRatio = tyreForce.slipRatio
    wheel.longitudinalForceN = wheel.contact ? tyreForce.longitudinal : 0
    wheel.lateralForceN = wheel.contact ? previousLateralForce + (tyreForce.targetLateral - previousLateralForce) * lateralBlend : 0
    const forceMagnitude = Math.hypot(wheel.longitudinalForceN, wheel.lateralForceN)
    if (forceMagnitude > frictionLimit && frictionLimit > 0) {
      const scale = frictionLimit / forceMagnitude
      wheel.longitudinalForceN *= scale
      wheel.lateralForceN *= scale
    }
    wheel.gripUsage = tyreForce.usage

    const rollingForce = wheel.contact && Math.abs(patchLong) > 0.05
      ? -Math.sign(patchLong) * vehicle.tyres.rollingResistance * wheel.normalLoadN : 0
    const forceBodyLong = (wheel.longitudinalForceN + rollingForce) * steerCos - wheel.lateralForceN * steerSin
    const forceBodyLat = (wheel.longitudinalForceN + rollingForce) * steerSin + wheel.lateralForceN * steerCos
    bodyLongitudinalForce += forceBodyLong
    bodyLateralForce += forceBodyLat
    yawMoment += wheel.longitudinalPositionM * forceBodyLat - wheel.lateralPositionM * forceBodyLong
    if (!wheel.contact && wheel.brakeTorqueNm === 0) wheel.angularVelocityRadPerSecond *= Math.exp(-0.05 * delta)
    wheel.rotationRadians += wheel.angularVelocityRadPerSecond * delta
  }

  if (Math.abs(longitudinal) > 0.01) {
    bodyLongitudinalForce -= Math.sign(longitudinal) * 0.5 * airDensity * vehicle.aero.dragArea * longitudinal ** 2
  }
  state.velocityX += (sin * bodyLongitudinalForce + cos * bodyLateralForce) / mass * delta
  state.velocityZ += (cos * bodyLongitudinalForce - sin * bodyLateralForce) / mass * delta
  state.yawRate += yawMoment / yawInertia * delta
  if (state.wheels.some((wheel) => wheel.contact) && Math.hypot(state.velocityX, state.velocityZ) < 0.025
    && propulsion === 0 && brakeInput === 0) {
    state.velocityX = 0; state.velocityZ = 0; state.yawRate = 0
  }

  const newLocal = localSpeeds(state, heading)
  state.longitudinalAcceleration = bodyLongitudinalForce / mass
  state.lateralAcceleration = bodyLateralForce / mass
  state.longitudinalSpeed = newLocal.longitudinal
  state.lateralSpeed = newLocal.lateral
  state.longitudinalForceN = bodyLongitudinalForce
  state.lateralForceN = bodyLateralForce
  state.frontSlipAngleDegrees = (state.wheels[0].slipAngleDegrees + state.wheels[1].slipAngleDegrees) * 0.5
  state.rearSlipAngleDegrees = (state.wheels[2].slipAngleDegrees + state.wheels[3].slipAngleDegrees) * 0.5
  state.frontLateralForceN = state.wheels[0].lateralForceN + state.wheels[1].lateralForceN
  state.rearLateralForceN = state.wheels[2].lateralForceN + state.wheels[3].lateralForceN
  state.frontGripUsage = Math.max(state.wheels[0].gripUsage, state.wheels[1].gripUsage)
  state.rearGripUsage = Math.max(state.wheels[2].gripUsage, state.wheels[3].gripUsage)
}

export function stepKunosLite(state: KunosLiteState, vehicle: KunosLiteVehicle, input: KunosLiteInput, heading: number, delta: number): KunosLiteStep {
  const steps = Math.max(1, Math.ceil(delta / (1 / 240)))
  const substep = delta / steps
  let integratedHeading = heading
  let deltaX = 0
  let deltaZ = 0
  for (let index = 0; index < steps; index += 1) {
    simulateSubstep(state, vehicle, input, integratedHeading, substep)
    integratedHeading += state.yawRate * substep
    deltaX += state.velocityX * substep
    deltaZ += state.velocityZ * substep
  }
  return { deltaX, deltaZ, headingDelta: integratedHeading - heading }
}
